import { useEffect, useState } from 'react';

import {
  DEFAULT_CONDITIONS,
  DEFAULT_GIFTCARD_REASONS,
  DEFAULT_REJECT_REASONS,
  DEFAULT_SENDBACK_STATES,
  DEFAULT_TRADEIN_SOURCES,
  DEFAULT_TRADEIN_STATES,
} from './constants';
import { fetchTradeinConstants } from './fetchTradeins';
import type { ConstantOption, RawTradeinConstants, TradeinConstants } from './types';

const FALLBACK: TradeinConstants = {
  states: DEFAULT_TRADEIN_STATES,
  sources: DEFAULT_TRADEIN_SOURCES,
  sendbackStates: DEFAULT_SENDBACK_STATES,
  conditions: DEFAULT_CONDITIONS,
  rejectReasons: DEFAULT_REJECT_REASONS,
  giftcardReasons: DEFAULT_GIFTCARD_REASONS,
};

/**
 * Enforce current Trove terminology on API-provided display names: hyphenate "Tradein" →
 * "Trade-in", capitalize "INSTi" → "INSTI", and drop the retired "MiTi" wording in favor of
 * "Digital Trade-in (DITI)". The patterns for the latter two are deliberately case-sensitive —
 * reware-api sends the display-cased constant values, so those are the spellings that occur.
 */
const normalizeLabels = (options: ConstantOption[]): ConstantOption[] =>
  options.map(({ code, displayName }) => ({
    code,
    displayName: displayName
      .replace(/Trade[\s-]?in/gi, 'Trade-in')
      .replace(/\bINSTi\b/g, 'INSTI')
      .replace(/\bMiTi\b/g, 'Digital Trade-in (DITI)'),
  }));

// Only the three lists whose wording drifted need rewriting; conditions, reject reasons and
// gift-card reasons pass through as the API sends them.
const normalizeConstants = (constants: TradeinConstants): TradeinConstants => ({
  ...constants,
  states: normalizeLabels(constants.states),
  sources: normalizeLabels(constants.sources),
  sendbackStates: normalizeLabels(constants.sendbackStates),
});

/**
 * Every list must be non-empty *and* every option must carry both strings. A partial payload is
 * worse than a failed one: it would be cached as success, so the dropdowns it covers render empty
 * with no fallback banner, chips fall back to raw codes, and the cache blocks any retry for the
 * session. Worse still, a missing list is not merely empty downstream — `giftcardReasons.map` in
 * the issue-gift-card dialog and `displayNameFor`'s `options.find` both throw during render,
 * white-screening the expand panel.
 *
 * The element check is not belt-and-braces. `code` and `displayName` are both nullable on the
 * wire; a single null `displayName` makes `normalizeLabels` throw inside the `.then`, which the
 * catch below then reports as a failed fetch — discarding five good lists and mislabelling a
 * data-shape bug as a network one.
 */
const isOptionList = (list: unknown): list is ConstantOption[] =>
  Array.isArray(list) &&
  list.length > 0 &&
  list.every(option => typeof option?.code === 'string' && typeof option?.displayName === 'string');

/**
 * Named per key rather than iterated with `Object.values`. A list that is *absent* rather than
 * null has no entry for `Object.values` to visit, so iterating values passes it vacuously — and
 * a payload missing a dropdown entirely is the shape most worth catching. A new constant needs
 * a line here; `RawTradeinConstants` guarantees it exists, not that anyone checked it.
 */
const usableConstants = (
  result: RawTradeinConstants | null | undefined
): result is TradeinConstants =>
  result != null &&
  isOptionList(result.states) &&
  isOptionList(result.sources) &&
  isOptionList(result.sendbackStates) &&
  isOptionList(result.conditions) &&
  isOptionList(result.rejectReasons) &&
  isOptionList(result.giftcardReasons);

let cache: TradeinConstants | null = null;

/**
 * Test seam, for the tests that are still outstanding. The cache has no other invalidation point
 * for the lifetime of the process, so without this each test would inherit whatever the previous
 * one left behind.
 */
export const resetTradeinConstantsCache = (): void => {
  cache = null;
};

/**
 * Trade-in states, sources, sendback states, and reasons for filters and dialogs. Serves
 * sensible defaults immediately (and permanently, if the constants query fails), refined from
 * the API once available. `isFallback` is true when we're stuck on defaults — callers surface
 * that, because the hardcoded lists are a strict subset of the real ones (barely a third of the
 * trade-in states, half the sendback states) and silently offering a subset makes a filtered
 * list read as complete.
 */
export const useTradeinConstants = (): TradeinConstants & { isFallback: boolean } => {
  const [constants, setConstants] = useState<TradeinConstants>(cache ?? FALLBACK);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    if (cache) return;
    let cancelled = false;
    fetchTradeinConstants()
      .then((result) => {
        if (!usableConstants(result)) {
          console.error('Trade-in constants came back unusable; keeping defaults:', result);
          if (!cancelled) setIsFallback(true);
          return;
        }
        cache = normalizeConstants(result);
        if (!cancelled) setConstants(cache);
      })
      .catch((error) => {
        console.error('Falling back to default trade-in filter options:', error);
        if (!cancelled) setIsFallback(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...constants, isFallback };
};
